import { Alert, Grid, Section, SnackbarManager } from '@cmg/design-system';
import { FormikProvider, useFormik } from 'formik';
import defaultTo from 'lodash/defaultTo';
import pick from 'lodash/pick';
import { useCallback, useMemo, useState } from 'react';

import { DescriptionList } from '../../../../../../design-system/body-sections/description-list/DescriptionList';
import { DescriptionListItem } from '../../../../../../design-system/body-sections/description-list/DescriptionListItem';
import { PageGridSectionActions } from '../../../../../../design-system/body-sections/PageGridSectionActions';
import type { SalesCoverageUnderwriterEmployeeDetailFragment } from '../graphql/__generated__';
import { useUpdateUnderwriterEmployee } from '../hooks/useUpdateUnderwriterEmployee';
import { contactInformationValidationSchema } from '../models/ContactInformationSection.model';
import type { UserDetailsFormValues } from '../models/UserDetailsSection.model';
import {
  getInitialUserDetailsValues,
  SECTIONS_SIZING,
  USER_DETAIL_FIELD_LABELS,
} from '../models/UserDetailsSection.model';
import { EmployeeContactInformationFields } from './EmployeeContactInformationFields';

export type ContactInformationSectionProps = {
  tenantId?: string;
  employee: SalesCoverageUnderwriterEmployeeDetailFragment;
};

export function ContactInformationSection({
  tenantId,
  employee,
}: Readonly<ContactInformationSectionProps>) {
  const [isEditing, setIsEditing] = useState(false);

  const initialValues = useMemo(() => getInitialUserDetailsValues(employee), [employee]);

  const { updateUnderwriterEmployee, error: mutationError } =
    useUpdateUnderwriterEmployee(tenantId);

  const handleSubmit = useCallback(
    async (values: UserDetailsFormValues) => {
      try {
        const employeeRestValues = pick(employee, [
          'name',
          'titles',
          'jobFunction',
          'department',
          'status',
        ]);
        const contactInformationValues = pick(values, [
          'address1',
          'address2',
          'email1',
          'email2',
          'phoneNumber1',
          'phoneNumber2',
          'faxNumber1',
          'faxNumber2',
        ]);

        if (!employee.employeeKey) {
          throw new Error('Employee key is missing.');
        }

        await updateUnderwriterEmployee({
          employeeKeyOrId: employee.employeeKey,
          input: {
            ...employeeRestValues,
            ...contactInformationValues,
          },
        });

        setIsEditing(false);
        SnackbarManager.success('Contact information updated successfully.');
      } catch {
        SnackbarManager.error('An error occurred while updating contact information.');
      }
    },
    [updateUnderwriterEmployee, employee]
  );

  const formik = useFormik<UserDetailsFormValues>({
    initialValues,
    validationSchema: contactInformationValidationSchema,
    onSubmit: handleSubmit,
  });

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    formik.resetForm();
  }, [formik]);

  const handleSave = useCallback(() => {
    formik.handleSubmit();
  }, [formik]);

  return (
    <FormikProvider value={formik}>
      <Grid item {...SECTIONS_SIZING}>
        <Section
          title="Contact Information"
          slotProps={{ content: { gap: 1 } }}
          data-testid="contact-information-section"
          actions={[
            <PageGridSectionActions
              key="page-grid-section-actions"
              isSubmitting={formik.isSubmitting}
              isEditing={isEditing}
              onEdit={() => setIsEditing(true)}
              onCancel={handleCancel}
              onSave={handleSave}
              canEdit
            />,
          ]}
        >
          {mutationError && <Alert severity="error">{mutationError.message}</Alert>}
          {isEditing ? (
            <EmployeeContactInformationFields data-testid="employee-contact-information-fields" />
          ) : (
            <DescriptionList>
              <DescriptionListItem
                label={USER_DETAIL_FIELD_LABELS.address1}
                value={defaultTo(employee.address1, '-')}
              />
              <DescriptionListItem
                label={USER_DETAIL_FIELD_LABELS.address2}
                value={defaultTo(employee.address2, '-')}
              />
              <DescriptionListItem
                label={USER_DETAIL_FIELD_LABELS.email1}
                value={defaultTo(employee.email1, '-')}
              />
              <DescriptionListItem
                label={USER_DETAIL_FIELD_LABELS.email2}
                value={defaultTo(employee.email2, '-')}
              />
              <DescriptionListItem
                label={USER_DETAIL_FIELD_LABELS.phoneNumber1}
                value={defaultTo(employee.phoneNumber1, '-')}
              />
              <DescriptionListItem
                label={USER_DETAIL_FIELD_LABELS.phoneNumber2}
                value={defaultTo(employee.phoneNumber2, '-')}
              />
              <DescriptionListItem
                label={USER_DETAIL_FIELD_LABELS.faxNumber1}
                value={defaultTo(employee.faxNumber1, '-')}
              />
              <DescriptionListItem
                label={USER_DETAIL_FIELD_LABELS.faxNumber2}
                value={defaultTo(employee.faxNumber2, '-')}
              />
            </DescriptionList>
          )}
        </Section>
      </Grid>
    </FormikProvider>
  );
}
